import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { CalendarDays, ChevronLeft, ChevronRight, Filter, Plus } from "lucide-react";
import { useSearch } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { FIELD_LEGEND_ITEMS, FIELD_FILTER_DOT } from "@shared/fieldCalendarColors";
import { singaporeCalendarDayBounds } from "@shared/timezone";
import { logFieldCalendarUrlCrash } from "./fieldCalendarDiagnostics";
import {
  buildFieldCalendarHref,
  canonicalFieldCalendarQuery,
  fieldCalendarUrlStateEquals,
  getEffectiveFieldCalendarSearch,
  parseFieldCalendarUrlSearch,
  safeFieldCalendarNavigateHref,
  toUrlState,
} from "./fieldCalendarUrlState";
import { FieldCalendarMonthWeek } from "./FieldCalendarMonthWeek";
import { FieldCalendarDispatchBoard } from "./FieldCalendarDispatchBoard";
import { FieldCalendarExecutionPanel } from "./FieldCalendarExecutionPanel";
import { FieldCalendarErrorBoundary } from "./FieldCalendarErrorBoundary";
import { CreateServiceVisitSheet, type CreateVisitDefaults } from "./CreateServiceVisitSheet";
import type { FieldCalendarViewMode, FieldVisitFilter } from "./fieldCalendarTypes";
import { logFieldCalendarCrash } from "./fieldCalendarDiagnostics";
import { partitionCalendarVisits } from "@shared/svrSchedulingIntegrity";
import { CalendarVisitsLoadError, SchedulingIncompleteSection } from "./CalendarVisitLoadState";
import {
  addDays,
  coerceValidDay,
  filterVisitsForFieldUser,
  groupVisitsByDate,
  queryRangeForView,
  sanitizeLeaveBlocks,
  sanitizeVisits,
  localDateInputValue,
  startOfDay,
  viewModeTitle,
} from "./fieldCalendarUtils";

const FIELD_ROLES = new Set(["service", "programming", "installer"]);
const SCROLL_STORAGE_KEY = "field-calendar-scroll-y";

function readInitialUrlState() {
  try {
    const search =
      typeof window === "undefined"
        ? ""
        : getEffectiveFieldCalendarSearch(window.location.search);
    return parseFieldCalendarUrlSearch(search);
  } catch (error) {
    logFieldCalendarUrlCrash("hydrate.initial", error, {});
    return parseFieldCalendarUrlSearch("");
  }
}

function safeVisitClick(id: unknown, returnHref: string): void {
  if (typeof id !== "number" || !Number.isFinite(id)) return;
  const ret = safeFieldCalendarNavigateHref(returnHref);
  try {
    sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
  } catch {
    /* ignore */
  }
  try {
    const returnTo = encodeURIComponent(ret);
    navigate(`/site-visits/id/${id}?returnTo=${returnTo}`, {
      state: { from: ret },
    });
  } catch (error) {
    logFieldCalendarUrlCrash("visitClick.navigate", error, { id, returnHref: ret });
    navigate(`/site-visits/id/${id}`);
  }
}

export function FieldCalendar() {
  const { user } = useAuth();
  const searchStr = useSearch();
  const isFieldRole = FIELD_ROLES.has(user?.role ?? "");

  const initialUrl = useMemo(() => readInitialUrlState(), []);
  const [viewMode, setViewMode] = useState<FieldCalendarViewMode>(initialUrl.view);
  const [anchorDate, setAnchorDate] = useState(initialUrl.anchorDate);
  const [selectedDay, setSelectedDay] = useState(initialUrl.selectedDay);
  const [laneFilter, setLaneFilter] = useState<FieldVisitFilter>(initialUrl.laneFilter);
  const [mySchedule, setMySchedule] = useState(initialUrl.mySchedule);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<CreateVisitDefaults | undefined>();

  const today = useMemo(() => startOfDay(new Date()), []);

  const safeSelectedDay = useMemo(() => coerceValidDay(selectedDay), [selectedDay]);
  const safeAnchorDate = useMemo(() => coerceValidDay(anchorDate), [anchorDate]);

  const { startDate, endDate } = useMemo(() => {
    try {
      return queryRangeForView(viewMode, safeAnchorDate, safeSelectedDay);
    } catch (error) {
      logFieldCalendarCrash("queryRange", error, { viewMode });
      const { startInclusive, endExclusive } = singaporeCalendarDayBounds(today);
      return { startDate: startInclusive, endDate: endExclusive };
    }
  }, [viewMode, safeAnchorDate, safeSelectedDay, today]);

  const visitsQuery = trpc.siteVisits.teamCalendarVisits.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as any,
  );
  const visitsRaw = visitsQuery.isError ? [] : visitsQuery.data;
  const isLoading = visitsQuery.isLoading;
  const visitsLoadFailed = Boolean(visitsQuery.isError);
  const { data: leaveBlocksRaw } = trpc.siteVisits.teamCalendarLeaveBlocks.useQuery(
    { startDate, endDate },
    { keepPreviousData: true } as any,
  );
  const { data: allStaff } = trpc.siteVisits.listStaff.useQuery();
  const fieldStaff = useMemo(
    () => (allStaff ?? []).filter((u) => FIELD_ROLES.has(u.role)),
    [allStaff],
  );

  const visits = useMemo(() => sanitizeVisits(visitsRaw), [visitsRaw]);
  const leaveBlocks = useMemo(() => sanitizeLeaveBlocks(leaveBlocksRaw), [leaveBlocksRaw]);

  const filteredVisits = useMemo(
    () =>
      filterVisitsForFieldUser(visits, {
        mySchedule,
        userId: user?.id,
        laneFilter: viewMode === "day" ? laneFilter : "all",
      }),
    [visits, mySchedule, user?.id, laneFilter, viewMode],
  );
  const { timed: timedVisits, incomplete: incompleteVisits } = useMemo(
    () => partitionCalendarVisits(filteredVisits),
    [filteredVisits],
  );

  const visitsByDate = useMemo(() => groupVisitsByDate(timedVisits), [timedVisits]);
  const visitCount = timedVisits.length;

  const reactUrlState = useMemo(
    () =>
      toUrlState({
        viewMode,
        selectedDay: safeSelectedDay,
        anchorDate: safeAnchorDate,
        mySchedule,
        laneFilter,
      }),
    [viewMode, safeSelectedDay, safeAnchorDate, mySchedule, laneFilter],
  );

  const calendarHref = useMemo(
    () => buildFieldCalendarHref(reactUrlState),
    [reactUrlState],
  );

  const desiredQueryFromState = useMemo(() => {
    const href = buildFieldCalendarHref(reactUrlState);
    const idx = href.indexOf("?");
    return idx >= 0 ? href.slice(idx + 1) : "";
  }, [reactUrlState]);

  const lastPushedQueryRef = useRef<string | null>(null);
  const skipNextHydrateRef = useRef(false);
  const reactUrlStateRef = useRef(reactUrlState);
  reactUrlStateRef.current = reactUrlState;

  useEffect(() => {
    const effective = getEffectiveFieldCalendarSearch(searchStr);
    const urlQuery = canonicalFieldCalendarQuery(effective);

    try {
      const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
      if (raw != null) {
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);
        const y = Number(raw);
        if (Number.isFinite(y)) {
          requestAnimationFrame(() => window.scrollTo(0, y));
        }
      }
    } catch {
      /* ignore */
    }

    if (skipNextHydrateRef.current && lastPushedQueryRef.current === urlQuery) {
      skipNextHydrateRef.current = false;
      lastPushedQueryRef.current = null;
      return;
    }

    let parsed = reactUrlStateRef.current;
    try {
      parsed = parseFieldCalendarUrlSearch(effective);
    } catch (error) {
      logFieldCalendarUrlCrash("hydrate.parse", error, {
        query: effective,
        selectedDay: localDateInputValue(reactUrlStateRef.current.selectedDay),
        href: calendarHref,
      });
      parsed = parseFieldCalendarUrlSearch("");
    }

    if (!fieldCalendarUrlStateEquals(parsed, reactUrlStateRef.current)) {
      logFieldCalendarUrlCrash("hydrate.apply", null, {
        query: effective,
        urlQuery,
        desiredQuery: desiredQueryFromState,
        selectedDay: localDateInputValue(parsed.selectedDay),
      });
      setViewMode(parsed.view);
      setSelectedDay(parsed.selectedDay);
      setAnchorDate(parsed.anchorDate);
      setMySchedule(parsed.mySchedule);
      setLaneFilter(parsed.laneFilter);
    }
  }, [searchStr]);

  useEffect(() => {
    const effective = getEffectiveFieldCalendarSearch(searchStr);
    const urlQuery = canonicalFieldCalendarQuery(effective);

    if (urlQuery === desiredQueryFromState) {
      lastPushedQueryRef.current = null;
      return;
    }

    try {
      lastPushedQueryRef.current = desiredQueryFromState;
      skipNextHydrateRef.current = true;
      navigate(calendarHref, { replace: true });
    } catch (error) {
      lastPushedQueryRef.current = null;
      skipNextHydrateRef.current = false;
      logFieldCalendarUrlCrash("sync.push", error, {
        query: effective,
        href: calendarHref,
        desiredQuery: desiredQueryFromState,
        urlQuery,
      });
    }
  }, [calendarHref, desiredQueryFromState, searchStr]);

  const shiftPeriod = (dir: -1 | 1) => {
    try {
      if (viewMode === "month") {
        setAnchorDate((d) => {
          const base = coerceValidDay(d);
          return new Date(base.getFullYear(), base.getMonth() + dir, 1);
        });
      } else if (viewMode === "week") {
        setAnchorDate((d) => addDays(coerceValidDay(d), dir * 7));
      } else {
        const next = addDays(safeSelectedDay, dir);
        setSelectedDay(next);
        setAnchorDate(next);
      }
    } catch (error) {
      logFieldCalendarCrash("shiftPeriod", error, { dir, viewMode });
    }
  };

  const selectDay = useCallback((day: Date) => {
    try {
      const d = coerceValidDay(day);
      setSelectedDay(d);
      setAnchorDate(d);
    } catch (error) {
      logFieldCalendarCrash("selectDay", error);
      setSelectedDay(today);
      setAnchorDate(today);
    }
  }, [today]);

  const openCreate = useCallback(
    (defaults?: CreateVisitDefaults) => {
      setCreateDefaults(
        defaults ?? {
          date: safeSelectedDay,
          dateLocal: localDateInputValue(safeSelectedDay),
        },
      );
      setCreateOpen(true);
    },
    [safeSelectedDay],
  );

  const filterPills: { id: FieldVisitFilter; label: string; dot?: string }[] = [
    { id: "all", label: `All ${visitCount}`, dot: FIELD_FILTER_DOT.all },
    { id: "service", label: "Service", dot: FIELD_FILTER_DOT.service },
    { id: "installation", label: "Install", dot: FIELD_FILTER_DOT.installation },
    { id: "programming", label: "Prog", dot: FIELD_FILTER_DOT.programming },
  ];

  const goToday = () => {
    setSelectedDay(today);
    setAnchorDate(today);
  };

  const legendItems = Array.isArray(FIELD_LEGEND_ITEMS) ? FIELD_LEGEND_ITEMS : [];

  return (
    <div className="relative bg-white pb-28">
      <header className="sticky top-0 z-20 border-b border-slate-300 bg-white shadow-sm">
        <div className="flex items-center gap-1 px-2 py-2">
          <button
            type="button"
            onClick={() => shiftPeriod(-1)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-800 active:bg-slate-100"
            aria-label="Previous period"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-center text-sm font-bold text-slate-900 sm:text-base">
            {viewModeTitle(viewMode)}
          </h1>
          <button
            type="button"
            onClick={() => shiftPeriod(1)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-800 active:bg-slate-100"
            aria-label="Next period"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="flex h-9 shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2 text-[10px] font-bold text-slate-700"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Today
          </button>
          {!isFieldRole && (
            <button
              type="button"
              onClick={() => navigate("/calendar")}
              className="shrink-0 rounded-md border border-slate-300 px-2 py-1.5 text-[10px] font-bold text-slate-600"
            >
              Grid
            </button>
          )}
        </div>

        <div className="flex border-y border-slate-200">
          {(["month", "week", "day"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setViewMode(mode);
                if (mode === "day") setSelectedDay(coerceValidDay(anchorDate));
              }}
              className={cn(
                "flex-1 py-2.5 text-center text-xs font-bold capitalize",
                viewMode === mode
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 active:bg-slate-50",
              )}
            >
              {mode}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-2 py-1.5">
          <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto">
            {legendItems.map((item) => (
              <span
                key={item.key}
                className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-slate-700"
              >
                <span className={cn("h-2 w-2 rounded-full", item.dot ?? "bg-slate-400")} />
                {item.label}
              </span>
            ))}
          </div>
          <Filter className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        </div>

        <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5">
          {viewMode === "day" &&
            filterPills.map((pill) => (
              <button
                key={pill.id}
                type="button"
                onClick={() => setLaneFilter(pill.id)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold",
                  laneFilter === pill.id
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700",
                )}
              >
                {pill.dot && <span className={cn("h-2 w-2 rounded-full", pill.dot)} />}
                {pill.label}
              </button>
            ))}
          <button
            type="button"
            onClick={() => setMySchedule((m) => !m)}
            className={cn(
              "rounded-full px-2.5 py-1 text-[11px] font-bold",
              viewMode === "day" ? "ml-auto" : "",
              mySchedule ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700",
            )}
          >
            {mySchedule ? "Mine only" : "Whole team"}
          </button>
        </div>
      </header>

      {visitsLoadFailed ? (
        <div className="px-3 pt-3">
          <CalendarVisitsLoadError onRetry={() => void visitsQuery.refetch()} />
        </div>
      ) : null}
      {!visitsLoadFailed && incompleteVisits.length > 0 ? (
        <div className="px-3 pt-3">
          <SchedulingIncompleteSection
            visits={incompleteVisits}
            onOpen={(id) => safeVisitClick(id, calendarHref)}
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex h-40 items-center justify-center text-sm font-semibold text-slate-500">
          Loading schedule…
        </div>
      ) : (
        <>
          <FieldCalendarErrorBoundary section="Calendar grid">
            {viewMode === "day" ? (
              <FieldCalendarDispatchBoard
                day={safeSelectedDay}
                visits={timedVisits}
                leaveBlocks={leaveBlocks}
                fieldStaff={fieldStaff}
                myScheduleUserId={mySchedule ? user?.id : undefined}
                onVisitClick={(id) => safeVisitClick(id, calendarHref)}
                onSelectDay={selectDay}
              />
            ) : (
              <FieldCalendarMonthWeek
                viewMode={viewMode}
                anchorDate={viewMode === "month" ? safeAnchorDate : coerceValidDay(anchorDate)}
                selectedDay={safeSelectedDay}
                visitsByDate={visitsByDate}
                leaveBlocks={leaveBlocks}
                today={today}
                myScheduleUserId={mySchedule ? user?.id : undefined}
                onSelectDay={selectDay}
              />
            )}
          </FieldCalendarErrorBoundary>

          <FieldCalendarErrorBoundary section="Day schedule list">
            <FieldCalendarExecutionPanel
              day={safeSelectedDay}
              visits={timedVisits}
              leaveBlocks={leaveBlocks}
              myScheduleUserId={mySchedule ? user?.id : undefined}
              onVisitClick={(id) => safeVisitClick(id, calendarHref)}
            />
          </FieldCalendarErrorBoundary>
        </>
      )}

      <button
        type="button"
        aria-label="Create service visit"
        onClick={() => openCreate()}
        className="fixed bottom-20 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg active:scale-95 sm:bottom-8 sm:right-8"
      >
        <Plus className="h-7 w-7 stroke-[2.5px]" />
      </button>

      <CreateServiceVisitSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaults={createDefaults}
        returnHref={calendarHref}
        onCreated={() => setCreateDefaults(undefined)}
      />
    </div>
  );
}
